import { useEffect, useMemo } from "react";
import { MapContainer, Marker, Popup, TileLayer, Tooltip, useMap } from "react-leaflet";
import L, { LatLngTuple } from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

interface Camion {
  longitude: string;
  latitude: string;
  data: string;
}

interface CustomOrderMapProps {
  camions: Camion[];
}

interface TruckPosition {
  position: LatLngTuple;
  label: string;
}

// Icône par défaut de Leaflet : les chemins d'images doivent être fournis explicitement sous webpack (CRA).
const truckIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Centre par défaut (Maroc) si aucune position n'est exploitable.
const DEFAULT_CENTER: LatLngTuple = [31.7917, -7.0926];

/** Recadre la carte sur l'ensemble des camions à chaque mise à jour des positions (rafraîchissement automatique). */
const FitToTrucks = ({ trucks }: { trucks: TruckPosition[] }) => {
  const map = useMap();

  useEffect(() => {
    if (trucks.length === 0) return;
    if (trucks.length === 1) {
      map.setView(trucks[0].position, 12);
      return;
    }
    map.fitBounds(L.latLngBounds(trucks.map((t) => t.position)), {
      padding: [30, 30],
      maxZoom: 13,
    });
  }, [map, trucks]);

  return null;
};

/**
 * Carte de localisation des camions, basée sur Leaflet et les tuiles OpenStreetMap
 * (gratuit, sans clé API). Remplace l'ancienne carte Google Maps.
 */
const CustomOrderMap = ({ camions }: CustomOrderMapProps) => {
  const trucks = useMemo<TruckPosition[]>(
    () =>
      camions
        .map((camion) => ({
          position: [Number(camion.latitude), Number(camion.longitude)] as LatLngTuple,
          label: camion.data,
        }))
        .filter(
          ({ position: [lat, lng] }) =>
            Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)
        ),
    [camions]
  );

  return (
    <MapContainer
      center={trucks[0]?.position ?? DEFAULT_CENTER}
      zoom={trucks.length > 0 ? 12 : 6}
      scrollWheelZoom={false}
      style={{ width: "100%", height: "300px", borderRadius: "5px" }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors'
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {trucks.map((truck) => (
        <Marker
          key={`${truck.label}-${truck.position[0]}-${truck.position[1]}`}
          position={truck.position}
          icon={truckIcon}
        >
          <Tooltip direction="top" offset={[0, -35]}>
            {truck.label}
          </Tooltip>
          <Popup>{truck.label}</Popup>
        </Marker>
      ))}
      <FitToTrucks trucks={trucks} />
    </MapContainer>
  );
};

export default CustomOrderMap;
